// ABOUTME: Presents the shared public header and footer and honest social sign-in readiness.
// ABOUTME: Email sign-in covers invited evaluation accounts and localhost development accounts.
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { BrandLogo, BrandMark } from "./BrandLogo";
import { Button, Notice } from "./ui/controls";
import { ArrowRight, ShieldCheck } from "./ui/icons";
import { message } from "../api";
import type { PublicConfig } from "../api";
import { authClient } from "../auth";
import { Field } from "./ui/controls";
import { TurnstileChallenge } from "./TurnstileChallenge";

export function PublicHeader({
  navigate,
}: {
  navigate: (path: string) => void;
}) {
  return (
    <header className="public-header">
      <a
        href="/"
        className="logo-link"
        aria-label="NovaName home"
        onClick={(event) => {
          event.preventDefault();
          navigate("/");
        }}
      >
        <BrandLogo />
      </a>
      <nav aria-label="Main navigation">
        <a href="/#product">Product</a>
        <a href="/#how-it-works">How it works</a>
        <a href="/#pricing">Pricing</a>
      </nav>
      <Button variant="inverse" onClick={() => navigate("/login")}>
        Get started
      </Button>
    </header>
  );
}

export function Login({
  navigate,
  config,
  configError,
  refreshConfig,
  onSessionChange,
}: {
  navigate: (path: string) => void;
  config: PublicConfig | null;
  configError: string;
  refreshConfig: () => void;
  onSessionChange: () => void;
}) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [verificationRevision, setVerificationRevision] = useState(0);
  const turnstileRequired =
    config?.turnstile?.enabled === true || config?.turnstile?.required === true;
  const evaluationEmail =
    config?.email_auth?.enabled === true &&
    config.email_auth.mode === "evaluation";
  const providers = ["google", "apple", "facebook"].map((id) => ({
    id,
    name: id.charAt(0).toUpperCase() + id.slice(1),
    readiness: (config?.auth_providers ?? config?.providers ?? []).find(
      (provider) => provider.id === id,
    ),
  }));
  async function signin(id: string) {
    if (turnstileRequired && !turnstileToken) {
      setError("Complete sign-in verification before choosing a provider.");
      return;
    }
    setBusy(id);
    setError("");
    try {
      const callbackURL = new URLSearchParams(location.search).has("client_id")
        ? location.href
        : `${location.origin}/app`;
      const result = await authClient.signIn.social({
        provider: id,
        callbackURL,
        fetchOptions: turnstileRequired
          ? { headers: { "x-turnstile-token": turnstileToken } }
          : undefined,
      });
      if (result.data?.url) location.assign(result.data.url);
      else
        throw new Error(
          result.error?.message ??
            "The identity provider did not return a sign-in URL. Please try again.",
        );
    } catch (reason) {
      setError(message(reason));
      setBusy("");
      if (turnstileRequired) {
        setTurnstileToken("");
        setVerificationRevision((value) => value + 1);
      }
    }
  }
  const noProviders =
    !!config && !providers.some((provider) => provider.readiness?.enabled);
  const visibleProviders = evaluationEmail
    ? providers.filter((provider) => provider.readiness?.enabled)
    : providers;
  const resetVerification = () => {
    setTurnstileToken("");
    setVerificationRevision((value) => value + 1);
  };
  return (
    <div className="public-page auth-page">
      <PublicHeader navigate={navigate} />
      <main className="auth-layout">
        <div className="auth-story">
          <span className="eyebrow">Your next chapter starts here</span>
          <h1>
            Ideas in.
            <br />
            Possibilities
            <br />
            <span className="word-underline">out.</span>
          </h1>
          <p>
            Keep your projects private.
            <br />
            Give your ideas room to grow.
          </p>
          <div className="auth-nameplate">
            <BrandMark />
            <span>
              Make a name
              <br />
              for yourself.
            </span>
          </div>
        </div>
        <section className="auth-panel" aria-labelledby="signin-title">
          <span className="tag">
            {evaluationEmail
              ? "Evaluation beta · free credits"
              : "Welcome to NovaName"}
          </span>
          <h2 id="signin-title">
            Find something
            <br />
            that feels like you.
          </h2>
          <p>
            Sign in to build a brief, explore names, and save your shortlist.
          </p>
          {error && <Notice kind="error">{error}</Notice>}
          {configError && (
            <Notice
              kind="error"
              action={
                <Button variant="quiet" onClick={refreshConfig}>
                  Retry
                </Button>
              }
            >
              {configError}
            </Notice>
          )}
          {evaluationEmail && (
            <EmailLogin
              mode="evaluation"
              invitationRequired={
                config.email_auth?.invitation_required === true
              }
              turnstileRequired={turnstileRequired}
              turnstileToken={turnstileToken}
              resetVerification={resetVerification}
              recoveryAvailable={
                config.email_auth?.password_recovery_available === true
              }
              verificationAvailable={
                config.email_auth?.verification_available === true
              }
              onSessionChange={onSessionChange}
            />
          )}
          {!!visibleProviders.length && (
            <div className="social-buttons">
              {visibleProviders.map((provider) => (
                <div key={provider.id}>
                  <Button
                    variant="secondary"
                    className="social-button"
                    disabled={
                      !provider.readiness?.enabled ||
                      !!busy ||
                      (turnstileRequired && !turnstileToken)
                    }
                    busy={busy === provider.id}
                    onClick={() => signin(provider.id)}
                  >
                    <span className="provider-monogram" aria-hidden="true">
                      {provider.name[0]}
                    </span>
                    Continue with {provider.name}
                    {!provider.readiness?.enabled && (
                      <span className="provider-state">
                        {config
                          ? "Not configured"
                          : configError
                            ? "Unavailable"
                            : "Loading"}
                      </span>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
          {turnstileRequired &&
            (!noProviders || evaluationEmail) &&
            (config?.turnstile?.site_key ? (
              <TurnstileChallenge
                key={verificationRevision}
                siteKey={config.turnstile.site_key}
                onTokenChange={setTurnstileToken}
              />
            ) : (
              <Notice kind="error">
                Sign-in verification is unavailable. Refresh this page or try
                again later.
              </Notice>
            ))}
          {noProviders && !evaluationEmail && (
            <Notice>
              Sign-in is being configured for this environment. Provider buttons
              will become available after credentials are verified.
            </Notice>
          )}
          {config?.local_email_auth && (
            <EmailLogin
              mode="local"
              onSessionChange={onSessionChange}
              recoveryAvailable={
                config.email_auth?.password_recovery_available === true
              }
              verificationAvailable={
                config.email_auth?.verification_available === true
              }
            />
          )}
          <p className="auth-privacy">
            <ShieldCheck />
            Your projects and personalized generations are private. Shared
            discovery never includes your brief or decisions.
          </p>
          <a
            className="back-link"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate("/");
            }}
          >
            Back to NovaName <ArrowRight />
          </a>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

export function PublicFooter() {
  return (
    <footer className="public-footer">
      <BrandLogo />
      <span>© {new Date().getFullYear()} NovaName</span>
    </footer>
  );
}

function EmailLogin({
  mode,
  invitationRequired = false,
  turnstileRequired = false,
  turnstileToken = "",
  resetVerification,
  recoveryAvailable = false,
  verificationAvailable = false,
  onSessionChange,
}: {
  mode: "local" | "evaluation";
  invitationRequired?: boolean;
  turnstileRequired?: boolean;
  turnstileToken?: string;
  resetVerification?: () => void;
  recoveryAvailable?: boolean;
  verificationAvailable?: boolean;
  onSessionChange: () => void;
}) {
  const evaluation = mode === "evaluation";
  const [invitation, setInvitation] = useState(() =>
    evaluation
      ? (new URLSearchParams(location.hash.slice(1)).get("invite") ?? "")
      : "",
  );
  const [signup, setSignup] = useState(
    () =>
      evaluation && new URLSearchParams(location.hash.slice(1)).has("invite"),
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgot, setForgot] = useState(false);
  const [feedback, setFeedback] = useState("");
  useEffect(() => {
    if (
      !evaluation ||
      !new URLSearchParams(location.hash.slice(1)).has("invite")
    )
      return;
    history.replaceState(
      history.state,
      "",
      `${location.pathname}${location.search}`,
    );
  }, [evaluation]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (turnstileRequired && !turnstileToken) return;
    setBusy(true);
    setError("");
    setFeedback("");
    try {
      const fetchOptions = evaluation
        ? {
            headers: {
              ...(turnstileRequired
                ? { "x-turnstile-token": turnstileToken }
                : {}),
              ...(signup && !forgot && invitationRequired
                ? { "x-beta-invite": invitation }
                : {}),
            },
          }
        : undefined;
      const result = forgot
        ? await authClient.requestPasswordReset({
            email,
            redirectTo: location.origin + "/reset-password",
            fetchOptions,
          })
        : signup
          ? await authClient.signUp.email({
              name,
              email,
              password,
              callbackURL: location.origin + "/app",
              fetchOptions,
            })
          : await authClient.signIn.email({ email, password, fetchOptions });
      if (result.error)
        throw new Error(
          result.error.message ?? "Email sign-in did not complete.",
        );
      if (forgot)
        setFeedback(
          "If this account is eligible, a password reset link will arrive by email. Check your spam folder too.",
        );
      else onSessionChange();
      setPassword("");
      if (signup) setInvitation("");
    } catch (reason) {
      setError(message(reason));
    } finally {
      setBusy(false);
      if (turnstileRequired) resetVerification?.();
    }
  }
  const form = (
    <form className="stack-form" onSubmit={submit}>
      {error && <Notice kind="error">{error}</Notice>}
      {feedback && <Notice kind="success">{feedback}</Notice>}
      {signup && !forgot && (
        <Field label="Your name">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="name"
            required
          />
        </Field>
      )}
      {signup && !forgot && invitationRequired && (
        <Field label="Invitation code">
          <input
            type="password"
            value={invitation}
            onChange={(event) => setInvitation(event.target.value)}
            autoComplete="off"
            maxLength={256}
            required
          />
        </Field>
      )}
      <Field label="Email">
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          required
        />
      </Field>
      {!forgot && (
        <Field label="Password" hint="At least 12 characters">
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete={signup ? "new-password" : "current-password"}
            minLength={12}
            required
          />
        </Field>
      )}
      <Button
        type="submit"
        busy={busy}
        disabled={turnstileRequired && !turnstileToken}
      >
        {forgot
          ? "Send password reset link"
          : evaluation
            ? signup
              ? "Create beta account"
              : "Sign in with email"
            : signup
              ? "Create local account"
              : "Sign in locally"}
      </Button>
      <Button
        variant="quiet"
        type="button"
        disabled={busy}
        onClick={() => {
          setError("");
          setFeedback("");
          setPassword("");
          if (forgot) setForgot(false);
          else setSignup((value) => !value);
        }}
      >
        {forgot
          ? "Back to sign in"
          : evaluation
            ? signup
              ? "Use an existing beta account"
              : "Create a beta account"
            : signup
              ? "Use an existing local account"
              : "Create a local account"}
      </Button>
      {recoveryAvailable && !signup && !forgot && (
        <Button
          variant="quiet"
          type="button"
          disabled={busy}
          onClick={() => {
            setForgot(true);
            setPassword("");
            setError("");
            setFeedback("");
          }}
        >
          Forgot your password?
        </Button>
      )}
    </form>
  );
  return evaluation ? (
    <section className="beta-login" aria-label="Evaluation email account">
      <p className="fine-print">
        {verificationAvailable && recoveryAvailable
          ? "Verify your email from the link sent when you create an account. You can request another link in account settings."
          : "Email verification and password recovery are not available during this evaluation."}
      </p>
      {form}
    </section>
  ) : (
    <details className="local-login">
      <summary>Local development account</summary>
      <p className="fine-print">
        Email sign-in is enabled only on this local development environment.
      </p>
      {form}
    </details>
  );
}
